export interface CreatedJobFlowCallbacks {
    setCreatedJobId: (jobId: string) => void;
    onSuccess: (jobId: string) => Promise<void>;
    onClose: () => void;
}

/** Finish creation in a deterministic order and preserve workspace mode. */
export async function completeCreatedJobFlow(
    jobId: string,
    continueInWorkspace: boolean,
    callbacks: CreatedJobFlowCallbacks,
): Promise<void> {
    if (!jobId.trim()) throw new Error('The job was created but no job ID was returned.');

    if (continueInWorkspace) {
        callbacks.setCreatedJobId(jobId);
        await callbacks.onSuccess(jobId);
        return;
    }

    await callbacks.onSuccess(jobId);
    callbacks.onClose();
}

export interface JobWorkspaceIdentity {
    id: string;
    created_at: string;
    invoice_id?: string;
    locked?: boolean;
    items: readonly unknown[];
}

export interface JobWorkspaceHandlers {
    onAddPart: (jobId: string, jobCreatedAt: string) => void;
    onAddLabour: (jobId: string, jobCreatedAt: string) => void;
    onWalkaround?: (jobId: string) => void;
    onGenerateInvoice?: (jobId: string, invoiceId?: string) => void;
}

/** Bind every post-create action to the same persisted job identity. */
export function jobWorkspaceActions(job: JobWorkspaceIdentity, handlers: JobWorkspaceHandlers) {
    return {
        addPart: () => handlers.onAddPart(job.id, job.created_at),
        addLabour: () => handlers.onAddLabour(job.id, job.created_at),
        walkaround: () => handlers.onWalkaround?.(job.id),
        invoice: () => handlers.onGenerateInvoice?.(job.id, job.invoice_id),
    };
}

/** New invoices require work; an existing invoice remains openable. */
export function canOpenJobInvoice(job: JobWorkspaceIdentity): boolean {
    return !!job.invoice_id || (!job.locked && job.items.length > 0);
}

/**
 * Whether a job's lines are fit to go on an invoice.
 *
 * "+ Part" and "+ Labour" save a real line straight away — a part with no
 * description at £0, a labour line called "Labour Charge" at the default rate
 * — so the mechanic can fill it in on the card. Nothing stopped an untouched
 * one being invoiced, and an invoice is the customer's legal document.
 *
 * Blocking: a line with no description (it would print blank) or no quantity.
 * Warnings, which the user can confirm past: a £0 line (a customer-supplied
 * part or goodwill can be genuine) and a labour line still carrying its
 * automatic name.
 */
export const DEFAULT_LABOUR_DESCRIPTION = 'Labour Charge';

export interface InvoiceLineLike {
    type: 'part' | 'labour' | 'service' | string;
    description?: string | null;
    quantity?: number | null;
    price?: number | null;
}

export interface InvoiceLineIssue {
    /** 1-based position among the job's lines, as the user sees them. */
    line: number;
    label: string;
    problem: string;
}

export function invoiceReadiness(lines: readonly InvoiceLineLike[]): {
    blocking: InvoiceLineIssue[];
    warnings: InvoiceLineIssue[];
} {
    const blocking: InvoiceLineIssue[] = [];
    const warnings: InvoiceLineIssue[] = [];
    lines.forEach((l, i) => {
        const kind = l.type === 'labour' ? 'Labour' : l.type === 'service' ? 'Service' : 'Part';
        const desc = (l.description ?? '').trim();
        const label = desc ? `${kind}: ${desc}` : `${kind} line ${i + 1}`;
        if (!desc) blocking.push({ line: i + 1, label, problem: 'has no description' });
        if (!(Number(l.quantity) > 0)) blocking.push({ line: i + 1, label, problem: 'has no quantity' });
        if (desc && !(Number(l.price) > 0)) warnings.push({ line: i + 1, label, problem: 'is priced at £0' });
        if (l.type === 'labour' && desc === DEFAULT_LABOUR_DESCRIPTION) {
            warnings.push({ line: i + 1, label, problem: 'still has the automatic name' });
        }
    });
    return { blocking, warnings };
}
